'use client'

import React, { useEffect, useRef, useState } from 'react'
import { Calendar, MapPin, Download, Mail } from 'lucide-react'
import type { CampAnmeldung, Teilnehmer, Camp } from '@/payload-types'
import {
  sendPaymentRemindersAction,
  type SendPaymentRemindersResult,
} from '@/actions/paymentReminder'
import { sendCampReminderEmailsAction } from '@/actions/campReminder'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

type PopulatedAnmeldung = CampAnmeldung & {
  teilnehmer: Teilnehmer
  camp: Camp
  alternativeCamp?: Camp | null
}

type ExportVariant = 'participants' | 'submitted' | 'rejected' | 'all'

interface CampInfoHeaderProps {
  camp: Camp
  anmeldungen: PopulatedAnmeldung[]
  selectedStatus?: string | null
  onStatusFilter?: (status: string | null) => void
}

const CampInfoHeader: React.FC<CampInfoHeaderProps> = ({
  camp,
  anmeldungen,
  selectedStatus,
  onStatusFilter,
}) => {
  const [isDownloading, setIsDownloading] = useState(false)
  const [isSendingReminders, setIsSendingReminders] = useState(false)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [isResultDialogOpen, setIsResultDialogOpen] = useState(false)
  const [result, setResult] = useState<SendPaymentRemindersResult | null>(null)
  const [isSendingCampReminders, setIsSendingCampReminders] = useState(false)
  const [isCampReminderConfirmDialogOpen, setIsCampReminderConfirmDialogOpen] = useState(false)
  const [isCampReminderResultDialogOpen, setIsCampReminderResultDialogOpen] = useState(false)
  const [campReminderResult, setCampReminderResult] = useState<SendPaymentRemindersResult | null>(
    null,
  )
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false)
  const exportMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setIsExportMenuOpen(false)
      }
    }

    if (isExportMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isExportMenuOpen])

  // Calculate status breakdown
  const statusBreakdown = anmeldungen.reduce(
    (acc, anmeldung) => {
      const status = anmeldung.status || 'unknown'
      acc[status] = (acc[status] || 0) + 1
      return acc
    },
    {} as Record<string, number>,
  )

  const confirmedCount = statusBreakdown.confirmed || 0
  const waitlistCount = statusBreakdown.waitlist || 0
  const submittedCount = statusBreakdown.submitted || 0
  const rejectedCount = statusBreakdown.rejected || 0
  const totalAnmeldungen = anmeldungen.length
  const hasParticipantExportData = confirmedCount + waitlistCount > 0

  const exportEmptyMessages: Record<ExportVariant, string> = {
    participants: 'Keine bestätigten oder Wartelisten-Anmeldungen für dieses Camp gefunden.',
    submitted: 'Keine eingereichten Anmeldungen für dieses Camp gefunden.',
    rejected: 'Keine abgelehnten Anmeldungen für dieses Camp gefunden.',
    all: 'Keine Anmeldungen für dieses Camp gefunden.',
  }

  const exportOptions = [
    {
      variant: 'participants' as const,
      label: 'Teilnehmerliste als CSV',
      availableDescription: 'Enthält bestätigte und Wartelisten-Anmeldungen',
      unavailableDescription: 'Keine bestätigten oder Wartelisten-Anmeldungen',
      isAvailable: hasParticipantExportData,
    },
    {
      variant: 'submitted' as const,
      label: 'Eingereichte Anmeldungen',
      availableDescription: 'Alle aktuellen Einsendungen mit Status "Eingereicht"',
      unavailableDescription: 'Keine eingereichten Anmeldungen',
      isAvailable: submittedCount > 0,
    },
    {
      variant: 'rejected' as const,
      label: 'Abgelehnte Anmeldungen',
      availableDescription: 'Alle abgelehnten Anmeldungen',
      unavailableDescription: 'Keine abgelehnten Anmeldungen',
      isAvailable: rejectedCount > 0,
    },
    {
      variant: 'all' as const,
      label: 'Alle Anmeldungen (inkl. intern)',
      availableDescription: 'Beinhaltet alle Anmeldungen',
      unavailableDescription: 'Keine Anmeldungen vorhanden',
      isAvailable: totalAnmeldungen > 0,
    },
  ]

  const variantFallbackFilenames: Record<ExportVariant, string> = {
    participants: 'teilnehmer',
    submitted: 'eingereichte-anmeldungen',
    rejected: 'abgelehnte-anmeldungen',
    all: 'alle-anmeldungen',
  }

  const parseErrorResponse = async (response: Response, fallbackMessage: string) => {
    try {
      const data = await response.json()
      if (typeof data?.error === 'string') {
        return data.error
      }
      if (typeof data?.message === 'string') {
        return data.message
      }
    } catch {
      // ignore JSON parse errors and use fallback
    }
    return fallbackMessage
  }

  const handleDownloadCSV = async (variant: ExportVariant = 'participants') => {
    setIsDownloading(true)
    try {
      const requestUrl = new URL(`/api/admin/camps/${camp.id}/export-csv`, window.location.origin)
      if (variant !== 'participants') {
        requestUrl.searchParams.set('variant', variant)
      }
      const response = await fetch(requestUrl.toString())
      if (!response.ok) {
        const fallbackMessage =
          response.status === 404
            ? exportEmptyMessages[variant]
            : 'Fehler beim Herunterladen der CSV-Datei'
        const message = await parseErrorResponse(response, fallbackMessage)
        alert(message)
        return
      }

      // Get the blob data
      const blob = await response.blob()

      // Format camp date for filename
      const campDate = camp.date ? new Date(camp.date).toISOString().split('T')[0] : 'no-date'
      const today = new Date().toISOString().split('T')[0]
      const contentDisposition = response.headers.get('Content-Disposition')
      let suggestedFilename = `${camp.title}_${campDate}_${variantFallbackFilenames[variant]}_Stand_${today}.csv`

      if (contentDisposition) {
        const filenameMatch = /filename="?([^"]+)"?/i.exec(contentDisposition)
        if (filenameMatch?.[1]) {
          suggestedFilename = filenameMatch[1]
        }
      }

      // Create a download link
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = suggestedFilename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Error downloading CSV:', error)
      alert('Fehler beim Herunterladen der CSV-Datei')
    } finally {
      setIsDownloading(false)
    }
  }

  const handleSendPaymentRemindersClick = () => {
    setIsConfirmDialogOpen(true)
  }

  const handleConfirmSendPaymentReminders = async () => {
    setIsSendingReminders(true)
    try {
      const result = await sendPaymentRemindersAction(camp.id)
      setResult(result)
      setIsConfirmDialogOpen(false)
      setIsResultDialogOpen(true)
      if (!result.success && result.errors && result.errors.length > 0) {
        console.error('Payment reminder errors:', result.errors)
      }
    } catch (error) {
      console.error('Error sending payment reminders:', error)
      setResult({
        success: false,
        message: 'Fehler beim Senden der Zahlungserinnerungen',
        totalEligible: 0,
        emailsSent: 0,
        emailsFailed: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error'],
      })
      setIsConfirmDialogOpen(false)
      setIsResultDialogOpen(true)
    } finally {
      setIsSendingReminders(false)
    }
  }

  const handleSendCampRemindersClick = () => {
    setIsCampReminderConfirmDialogOpen(true)
  }

  const handleConfirmSendCampReminders = async () => {
    setIsSendingCampReminders(true)
    try {
      const reminderResult = await sendCampReminderEmailsAction(camp.id)
      setCampReminderResult(reminderResult)
      setIsCampReminderConfirmDialogOpen(false)
      setIsCampReminderResultDialogOpen(true)
      if (!reminderResult.success && reminderResult.errors?.length) {
        console.error('Camp reminder errors:', reminderResult.errors)
      }
    } catch (error) {
      console.error('Error sending camp reminders:', error)
      setCampReminderResult({
        success: false,
        message: 'Fehler beim Senden der Camp-Erinnerungen',
        totalEligible: 0,
        emailsSent: 0,
        emailsFailed: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error'],
      })
      setIsCampReminderConfirmDialogOpen(false)
      setIsCampReminderResultDialogOpen(true)
    } finally {
      setIsSendingCampReminders(false)
    }
  }

  // Calculate equipment needs - only for confirmed bookings
  const equipmentNeeds = anmeldungen.filter(
    (anmeldung) => anmeldung.status === 'confirmed' && anmeldung.needsEquipment,
  ).length
  const liftTicketNeeds = anmeldungen.filter(
    (anmeldung) => anmeldung.status === 'confirmed' && anmeldung.needsLiftTicket,
  ).length
  const multiPitchInterestCount = anmeldungen.filter(
    (anmeldung) => anmeldung.status === 'confirmed' && anmeldung.interestMultiPitch,
  ).length

  // Calculate total participants including children
  const totalParticipants = anmeldungen.reduce((total, anmeldung) => {
    const childrenCount = anmeldung.children?.length || 0
    return total + 1 + childrenCount // 1 for main participant + children
  }, 0)

  // Calculate children count separately
  const totalChildren = anmeldungen.reduce((total, anmeldung) => {
    return total + (anmeldung.children?.length || 0)
  }, 0)

  // Calculate children count for confirmed registrations only
  const confirmedChildren = anmeldungen.reduce((total, anmeldung) => {
    if (anmeldung.status !== 'confirmed') return total
    return total + (anmeldung.children?.length || 0)
  }, 0)

  const statusLabels: Record<string, string> = {
    submitted: 'Eingereicht',
    confirmed: 'Bestätigt',
    rejected: 'Abgelehnt',
    waitlist: 'Warteliste',
    unknown: 'Unbekannt',
  }

  const statusColors: Record<string, string> = {
    submitted: 'bg-amber-100 text-amber-800',
    confirmed: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    waitlist: 'bg-gray-100 text-gray-800',
    unknown: 'bg-gray-100 text-gray-500',
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1 space-y-3">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">{camp.title}</h2>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {camp.date ? new Date(camp.date).toLocaleDateString('de-DE') : 'Date not set'}
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              {camp.location || 'Location not specified'}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative" ref={exportMenuRef}>
              <button
                onClick={() => setIsExportMenuOpen((open) => !open)}
                disabled={anmeldungen.length === 0}
                className="inline-flex cursor-pointer items-center gap-2 rounded-md border-0 bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all duration-150 hover:bg-blue-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
                title="Exportoptionen anzeigen"
                aria-haspopup="menu"
                aria-expanded={isExportMenuOpen}
              >
                <Download className="h-4 w-4" />
                {isDownloading ? 'Wird heruntergeladen...' : 'Exportieren'}
              </button>
              {isExportMenuOpen && (
                <div
                  className="absolute z-10 mt-2 w-64 rounded-md border border-gray-200 bg-white shadow-lg focus:outline-none"
                  role="menu"
                  aria-label="Exportoptionen"
                >
                  {exportOptions.map((option, index) => (
                    <React.Fragment key={option.variant}>
                      {index > 0 && <div className="border-t border-gray-100" />}
                      <button
                        onClick={async () => {
                          setIsExportMenuOpen(false)
                          await handleDownloadCSV(option.variant)
                        }}
                        className="flex w-full flex-col items-start gap-1 border-0 px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none disabled:cursor-not-allowed disabled:text-gray-400"
                        disabled={isDownloading || !option.isAvailable}
                      >
                        <span className="font-medium">{option.label}</span>
                        <span className="text-xs text-gray-500">
                          {option.isAvailable
                            ? option.availableDescription
                            : option.unavailableDescription}
                        </span>
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={handleSendPaymentRemindersClick}
              disabled={isSendingReminders || anmeldungen.length === 0}
              className="inline-flex cursor-pointer items-center gap-2 rounded-md border-0 bg-orange-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all duration-150 hover:bg-orange-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
              title="Zahlungserinnerungen versenden"
            >
              <Mail className="h-4 w-4" />
              {isSendingReminders ? 'Wird versendet...' : 'Zahlungserinnerungen versenden'}
            </button>
            <button
              onClick={handleSendCampRemindersClick}
              disabled={isSendingCampReminders || confirmedCount === 0}
              className="inline-flex cursor-pointer items-center gap-2 rounded-md border-0 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all duration-150 hover:bg-emerald-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
              title="Camp-Erinnerungen versenden"
            >
              <Mail className="h-4 w-4" />
              {isSendingCampReminders ? 'Wird versendet...' : 'Camp-Erinnerungen versenden'}
            </button>
          </div>
        </div>
        <div className="text-right">
          <div className="mb-3 flex items-center gap-6 border-b border-gray-200 pb-3">
            {/* Bestätigte Anmeldungen */}
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {statusBreakdown.confirmed || 0}

                {confirmedChildren > 0 &&
                  ` (+ ${confirmedChildren} ${confirmedChildren > 1 ? 'Kinder' : 'Kind'})`}
              </div>
              <div className="flex items-center gap-1 text-sm text-gray-600">
                Bestätigte Anmeldungen
              </div>
            </div>

            {/* Equipment Needs */}
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{equipmentNeeds}</div>
              <div className="flex items-center gap-1 text-sm text-gray-600">
                Leihausrüstung (confirmed)
              </div>
            </div>

            {/* Ticket Needs */}
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{liftTicketNeeds}</div>
              <div className="flex items-center gap-1 text-sm text-gray-600">
                Tickets (confirmed)
              </div>
            </div>

            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{multiPitchInterestCount}</div>
              <div className="flex items-center gap-1 text-sm text-gray-600">
                Mehrseillängen (confirmed)
              </div>
            </div>

            {/* Total Registrations */}
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{anmeldungen.length}</div>
              <div className="text-sm text-gray-600">Anmeldungen</div>
            </div>

            {/* Total Participants */}
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{totalParticipants}</div>
              <div className="text-sm text-gray-600">
                Teilnehmer{totalChildren > 0 && ` (+${totalChildren} Kinder)`}
              </div>
            </div>
          </div>

          {/* Status breakdown with clickable filters */}
          <div className="flex flex-wrap justify-end gap-2">
            {/* All status button */}
            <button
              onClick={() => onStatusFilter?.(null)}
              className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-2 border-transparent outline-none focus:outline-none ${
                selectedStatus === null
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Alle ({anmeldungen.length})
            </button>

            {/* Individual status buttons */}
            {Object.entries(statusBreakdown).map(([status, count]) => (
              <button
                key={status}
                onClick={() => onStatusFilter?.(status)}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-2 border-transparent outline-none focus:outline-none ${
                  selectedStatus === status
                    ? `${statusColors[status]} opacity-100 border-current`
                    : `${statusColors[status]} hover:opacity-80 opacity-70 `
                }`}
              >
                {statusLabels[status]} ({count})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog for Payment Reminders */}
      <Dialog
        open={isConfirmDialogOpen}
        onOpenChange={(open) => {
          if (!isSendingReminders) {
            setIsConfirmDialogOpen(open)
          }
        }}
      >
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Zahlungserinnerungen versenden</DialogTitle>
            <DialogDescription className="text-gray-700">
              {isSendingReminders
                ? 'Zahlungserinnerungen werden gerade versendet. Bitte warten Sie...'
                : 'Möchten Sie wirklich Zahlungserinnerungen an alle berechtigten Teilnehmer senden?'}
            </DialogDescription>
          </DialogHeader>
          {!isSendingReminders && (
            <div className="text-sm text-gray-700">
              <p className="mb-2">Es werden nur Teilnehmer kontaktiert, die:</p>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>den Status &quot;Bestätigt&quot; haben</li>
                <li>noch nicht bezahlt haben (Camp-Gebühr oder Ticket)</li>
                <li>noch keine Zahlungserinnerung erhalten haben</li>
              </ul>
            </div>
          )}
          {isSendingReminders && (
            <div className="flex items-center justify-center py-8">
              <div className="flex flex-col items-center space-y-4">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-orange-600"></div>
                <p className="text-sm text-gray-600">Zahlungserinnerungen werden versendet...</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <button
              onClick={() => setIsConfirmDialogOpen(false)}
              disabled={isSendingReminders}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              onClick={handleConfirmSendPaymentReminders}
              disabled={isSendingReminders}
              className="rounded-md border border-transparent bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSendingReminders ? 'Wird versendet...' : 'Ja, versenden'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Camp Reminders */}
      <Dialog
        open={isCampReminderConfirmDialogOpen}
        onOpenChange={(open) => {
          if (!isSendingCampReminders) {
            setIsCampReminderConfirmDialogOpen(open)
          }
        }}
      >
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Camp-Erinnerungen versenden</DialogTitle>
            <DialogDescription className="text-gray-700">
              {isSendingCampReminders
                ? 'Camp-Erinnerungen werden gerade versendet. Bitte warten Sie...'
                : 'Möchten Sie Erinnerungs-E-Mails an alle bestätigten Teilnehmer senden?'}
            </DialogDescription>
          </DialogHeader>
          {!isSendingCampReminders && (
            <div className="text-sm text-gray-700">
              <p className="mb-2">Es werden nur Teilnehmer kontaktiert, die:</p>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>den Status &quot;Bestätigt&quot; haben</li>
                <li>eine E-Mail-Adresse hinterlegt haben</li>
                <li>noch keine Camp-Erinnerung für dieses Camp erhalten haben</li>
              </ul>
            </div>
          )}
          {isSendingCampReminders && (
            <div className="flex items-center justify-center py-8">
              <div className="flex flex-col items-center space-y-4">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-emerald-600"></div>
                <p className="text-sm text-gray-600">Camp-Erinnerungen werden versendet...</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <button
              onClick={() => setIsCampReminderConfirmDialogOpen(false)}
              disabled={isSendingCampReminders}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              onClick={handleConfirmSendCampReminders}
              disabled={isSendingCampReminders}
              className="rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSendingCampReminders ? 'Wird versendet...' : 'Ja, versenden'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result Dialog */}
      {result && (
        <Dialog open={isResultDialogOpen} onOpenChange={setIsResultDialogOpen}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className={result.success ? 'text-gray-900' : 'text-red-600'}>
                {result.success ? 'Zahlungserinnerungen versendet' : 'Fehler beim Versenden'}
              </DialogTitle>
              <DialogDescription className="text-gray-700">
                {result.success
                  ? 'Die Zahlungserinnerungen wurden erfolgreich verarbeitet.'
                  : result.message}
              </DialogDescription>
            </DialogHeader>
            {result.success && result.emailsSent === 0 && result.emailsFailed === 0 && (
              <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-800">
                Es wurden keine E-Mails versendet, da keine berechtigten Teilnehmer gefunden wurden.
              </div>
            )}
            {result.success && (
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex items-center justify-between rounded-md bg-gray-50 p-3">
                  <span className="font-medium">Berechtigte Teilnehmer:</span>
                  <span className="font-semibold text-gray-900">{result.totalEligible}</span>
                </div>
                <div className="flex items-center justify-between rounded-md bg-green-50 p-3">
                  <span className="font-medium text-green-800">Erfolgreich versendet:</span>
                  <span className="font-semibold text-green-900">{result.emailsSent}</span>
                </div>
                {result.emailsFailed > 0 && (
                  <div className="flex items-center justify-between rounded-md bg-red-50 p-3">
                    <span className="font-medium text-red-800">Fehlgeschlagen:</span>
                    <span className="font-semibold text-red-900">{result.emailsFailed}</span>
                  </div>
                )}
                {result.errors && result.errors.length > 0 && (
                  <div className="rounded-md bg-yellow-50 p-3">
                    <p className="mb-2 font-medium text-yellow-800">Fehlerdetails:</p>
                    <ul className="list-inside list-disc space-y-1 text-xs text-yellow-900">
                      {result.errors.slice(0, 5).map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                      {result.errors.length > 5 && (
                        <li className="text-yellow-700">
                          ... und {result.errors.length - 5} weitere Fehler
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {!result.success && result.errors && result.errors.length > 0 && (
              <div className="rounded-md bg-red-50 p-3">
                <p className="mb-2 font-medium text-red-800">Fehlerdetails:</p>
                <ul className="list-inside list-disc space-y-1 text-sm text-red-900">
                  {result.errors.slice(0, 10).map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                  {result.errors.length > 10 && (
                    <li className="text-red-700">
                      ... und {result.errors.length - 10} weitere Fehler
                    </li>
                  )}
                </ul>
              </div>
            )}
            <DialogFooter>
              <button
                onClick={() => setIsResultDialogOpen(false)}
                className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Schließen
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Camp Reminder Result Dialog */}
      {campReminderResult && (
        <Dialog
          open={isCampReminderResultDialogOpen}
          onOpenChange={setIsCampReminderResultDialogOpen}
        >
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle
                className={campReminderResult.success ? 'text-gray-900' : 'text-red-600'}
              >
                {campReminderResult.success
                  ? 'Camp-Erinnerungen versendet'
                  : 'Fehler beim Versenden'}
              </DialogTitle>
              <DialogDescription className="text-gray-700">
                {campReminderResult.success
                  ? 'Die Camp-Erinnerungen wurden erfolgreich verarbeitet.'
                  : campReminderResult.message}
              </DialogDescription>
            </DialogHeader>
            {campReminderResult.success && (
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex items-center justify-between rounded-md bg-gray-50 p-3">
                  <span className="font-medium">Berechtigte Teilnehmer:</span>
                  <span className="font-semibold text-gray-900">
                    {campReminderResult.totalEligible}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-md bg-green-50 p-3">
                  <span className="font-medium text-green-800">Erfolgreich versendet:</span>
                  <span className="font-semibold text-green-900">
                    {campReminderResult.emailsSent}
                  </span>
                </div>
                {campReminderResult.emailsFailed > 0 && (
                  <div className="flex items-center justify-between rounded-md bg-red-50 p-3">
                    <span className="font-medium text-red-800">Fehlgeschlagen:</span>
                    <span className="font-semibold text-red-900">
                      {campReminderResult.emailsFailed}
                    </span>
                  </div>
                )}
                {campReminderResult.errors && campReminderResult.errors.length > 0 && (
                  <div className="rounded-md bg-yellow-50 p-3">
                    <p className="mb-2 font-medium text-yellow-800">Fehlerdetails:</p>
                    <ul className="list-inside list-disc space-y-1 text-xs text-yellow-900">
                      {campReminderResult.errors.slice(0, 5).map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                      {campReminderResult.errors.length > 5 && (
                        <li className="text-yellow-700">
                          ... und {campReminderResult.errors.length - 5} weitere Fehler
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {!campReminderResult.success && campReminderResult.errors && (
              <div className="rounded-md bg-red-50 p-3">
                <p className="mb-2 font-medium text-red-800">Fehlerdetails:</p>
                <ul className="list-inside list-disc space-y-1 text-sm text-red-900">
                  {campReminderResult.errors.slice(0, 10).map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                  {campReminderResult.errors.length > 10 && (
                    <li className="text-red-700">
                      ... und {campReminderResult.errors.length - 10} weitere Fehler
                    </li>
                  )}
                </ul>
              </div>
            )}
            <DialogFooter>
              <button
                onClick={() => setIsCampReminderResultDialogOpen(false)}
                className="rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                Schließen
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

export default CampInfoHeader
